/**
 * 单个活跃用户条目
 *
 * 显示：头像（含未读红点）+ 姓名 + 交互类型 + 48h 倒计时
 */

import { useState, useEffect } from 'react';
import { Avatar, cn } from '@heroui/react';
import type { ActiveUser } from '../hooks/useWechatApi';

/** 交互类型中文映射 */
const INTERACTION_TYPE_LABELS: Record<string, string> = {
  message: '发送了消息',
  menu_click: '点击了菜单',
  subscribe: '关注了公众号',
  scan: '扫描了二维码',
};

/** 格式化剩余时间为 HH:MM:SS */
function formatRemainingTime(ms: number): string {
  if (ms <= 0) return '已过期';
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

/** 获取显示名称（优先级：person.fullName > user.nickname > user.username） */
function getDisplayName(user: ActiveUser): string {
  if (user.person?.fullName) return user.person.fullName;
  if (user.user?.nickname) return user.user.nickname;
  if (user.user?.username) return user.user.username;
  return user.openid.substring(0, 8) + '...';
}

/** 获取头像名称首字母 */
function getInitials(user: ActiveUser): string {
  const name = getDisplayName(user);
  return name.charAt(0).toUpperCase();
}

interface ActiveUserItemProps {
  user: ActiveUser;
  isSelected: boolean;
  hasUnread: boolean;
  onClick: () => void;
}

export function ActiveUserItem({ user, isSelected, hasUnread, onClick }: ActiveUserItemProps) {
  // 倒计时：基于 expiresAt 每秒更新
  const [remainingMs, setRemainingMs] = useState(() => {
    const expires = new Date(user.expiresAt).getTime();
    return Math.max(0, expires - Date.now());
  });

  useEffect(() => {
    const expires = new Date(user.expiresAt).getTime();
    setRemainingMs(Math.max(0, expires - Date.now()));

    const timer = setInterval(() => {
      const remaining = expires - Date.now();
      if (remaining <= 0) {
        setRemainingMs(0);
        clearInterval(timer);
      } else {
        setRemainingMs(remaining);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [user.expiresAt]);

  const isExpired = remainingMs <= 0;
  const displayName = getDisplayName(user);
  const interactionLabel = INTERACTION_TYPE_LABELS[user.interactionType] || user.interactionType;

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-3 px-4 py-3 text-left transition-colors',
        'hover:bg-accent/50',
        isSelected && 'bg-accent',
        isExpired && 'opacity-50',
      )}
    >
      {/* 头像 + 未读红点 */}
      <div className="relative shrink-0">
        <Avatar className="size-10" variant="soft">
          {user.user?.avatarUrl ? (
            <Avatar.Image src={user.user.avatarUrl} alt={displayName} />
          ) : null}
          <Avatar.Fallback className="bg-primary/10 text-sm text-primary">{getInitials(user)}</Avatar.Fallback>
        </Avatar>
        {hasUnread && (
          <span className="absolute -right-0.5 -top-0.5 size-3 rounded-full border-2 border-background bg-red-500" />
        )}
      </div>

      {/* 中间：姓名 */}
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium">{displayName}</div>
      </div>

      {/* 右侧：交互类型 + 倒计时 */}
      <div className="shrink-0 text-right">
        <div className="max-w-[80px] truncate text-xs text-muted-foreground">{interactionLabel}</div>
        <div
          className={cn('mt-0.5 text-xs tabular-nums', isExpired ? 'text-red-500' : 'text-muted-foreground')}
        >
          {formatRemainingTime(remainingMs)}
        </div>
      </div>
    </button>
  );
}
